'use client'

import { useMemo } from 'react'
import {
  calculateLastInteraction,
  getEntriesForPerson,
  resolveLinkedPeople,
  useDocumentView,
  useDocumentStatus,
} from '@/document'
import type { Entry, EntryWithPeople } from '@/document'

export type PersonWithLastInteraction = Entry & {
  lastInteraction: string | null
  lastContactAt: Date | undefined
}

export const peopleQueryKey = ['people'] as const
export const personEntriesQueryKey = (personId: string) =>
  ['person-entries', personId] as const

export function usePeopleData() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const data = useMemo(() => {
    if (!doc) return []

    // Filter Entry[] by type='person' excluding archived
    const personEntries = Object.values(doc.entries)
      .filter((entry) => !entry.archivedAt)
      .filter((e) => e.type === 'person')

    // Add lastInteraction and lastContactAt to each person
    const people: PersonWithLastInteraction[] = personEntries.map((entry) => {
      const lastInteraction = calculateLastInteraction(entry.id, doc)
      const lastContactAt = lastInteraction
        ? new Date(lastInteraction)
        : undefined

      return {
        ...entry,
        lastInteraction,
        lastContactAt,
      }
    })

    // Sort by most recent contact (DESC), people with no contact at end
    return people.sort((a, b) => {
      if (!a.lastContactAt && !b.lastContactAt)
        return a.title.localeCompare(b.title)
      if (!a.lastContactAt) return 1
      if (!b.lastContactAt) return -1
      return b.lastContactAt.getTime() - a.lastContactAt.getTime()
    })
  }, [doc])

  return {
    data,
    isLoading: !ready,
    error: null,
    isSuccess: ready,
    isError: false,
  }
}

/**
 * Prefetch people data.
 * Note: With client-side IndexedDB, prefetching is less critical
 * but kept for API compatibility.
 */
export function usePrefetchPeople() {
  // No-op since data is already local
  return () => {}
}

/**
 * Invalidate people data cache.
 * Note: With reactive Automerge docs, this is less needed
 * but kept for API compatibility.
 */
export function useInvalidatePeopleData() {
  // No-op since data reactively updates via useAutomergeAdapter
  return () => {}
}

export function usePersonEntries(personId: string | null) {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const data = useMemo(() => {
    if (!personId || !doc) return null

    // Get all entries linked to this person
    const linkedEntries = getEntriesForPerson(personId, doc)

    // Helper to attach people and topics to entries
    const withPeople = (entry: Entry): EntryWithPeople => {
      const linkedPeople = resolveLinkedPeople(entry.id, doc)
      const people = linkedPeople.map((p) => ({
        id: p.id,
        name: p.title,
      }))

      // Resolve topics from topicIds
      const topics = (entry.topicIds || [])
        .map((topicId) => doc.topics[topicId])
        .filter((topic) => topic)
        .map((topic) => ({
          id: topic.id,
          name: topic.name,
        }))

      return { ...entry, people, topics }
    }

    // Separate by type with people
    const journals = linkedEntries
      .filter((e) => e.type === 'journal')
      .map(withPeople)

    const tasks = linkedEntries.filter((e) => e.type === 'task').map(withPeople)

    const movies = linkedEntries
      .filter((e) => e.type === 'movie')
      .map(withPeople)

    const books = linkedEntries.filter((e) => e.type === 'book').map(withPeople)

    const artists = linkedEntries
      .filter((e) => e.type === 'artist')
      .map(withPeople)

    const labels = linkedEntries
      .filter((e) => e.type === 'label')
      .map(withPeople)

    const releases = linkedEntries
      .filter((e) => e.type === 'album')
      .map(withPeople)

    // Calculate last interaction
    const lastInteraction = calculateLastInteraction(personId, doc)

    return {
      tasks,
      journals,
      movies,
      books,
      artists,
      labels,
      releases,
      lastInteraction,
    }
  }, [personId, doc])

  return {
    data,
    isLoading: !ready,
    error: null,
    isSuccess: ready && !!personId,
    isError: false,
    // No-op refetch since data updates reactively via Automerge
    refetch: () => {},
  }
}

/**
 * Prefetch person entries.
 * Note: With client-side IndexedDB, prefetching is less critical
 * but kept for API compatibility.
 */
export function usePrefetchPersonEntries() {
  // No-op since data is already local
  return (personId: string) => {}
}

/**
 * Invalidate person entries cache.
 * Note: With reactive Automerge docs, this is less needed
 * but kept for API compatibility.
 */
export function useInvalidatePersonEntries() {
  // No-op since data reactively updates via useAutomergeAdapter
  return (personId?: string) => {}
}
